import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import ImageGallery from 'components/Luxkit/Carousel/ImageGallery'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import { rem } from 'polished'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import OfferBadge from 'components/Common/OfferBadge'
import { isBedbank, isCruiseOffer, isLEOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import { mediaQueryUp } from 'components/utils/breakpoint'
import variables from 'styles/vendor/bootstrap/variables'
import { ImageCarouselInterstitial } from 'components/Luxkit/Carousel/BaseImageCarousel'
import VimeoVideo from 'components/Common/Video/VimeoVideo'
import Clickable from 'components/Common/Clickable'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import LinePlayIcon from 'components/Luxkit/Icons/line/LinePlayIcon'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { getApplicableVelocityFeatures } from 'lib/partnership/partnershipUtils'
import GeoContext from 'contexts/geoContext'
import { VIRGIN_VELOCITY_PARTNERSHIP_CODE } from 'constants/partnerships'
import PartnershipLabel from 'components/Luxkit/Label/PartnershipLabel'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage'
import ImageGalleryLightbox from 'components/Luxkit/Carousel/ImageGalleryLightbox'
import TextButton from 'components/Luxkit/Button/TextButton'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import LineImagesIcon from 'components/Luxkit/Icons/line/LineImagesIcon'
import { take } from 'lib/array/arrayUtils'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Image from 'components/Common/Image'
import { isMobile } from 'lib/web/deviceUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton, { ExperienceBookmarkButton, TourBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import ShareModal from 'components/Common/Modals/ShareModal'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'

const PlayButton = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transition: transform 0.2s;
  transform: translateY(-50%) translateX(-50%) scale(1);
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(80)};
  width: ${rem(80)};

  >:first-child {
    height: ${rem(48)};
    width: ${rem(48)};
  }

  ${mediaQueryUp.tablet} {
    height: ${rem(108)};
    width: ${rem(108)};

    >:first-child {
      height: ${rem(60)};
      width: ${rem(60)};
    }
  }
`

const VimeoWrapper = styled(Clickable)`
  height: 100%;
  width: 100%;
  position: relative;
  background-color: ${props => props.theme.palette.neutral.default.one};

  > * {
    pointer-events: none;
  }

  &:focus, &:hover {
    > ${PlayButton} {
      transform: translateY(-50%) translateX(-50%) scale(1.2);
    }
  }
`

const MobileHuggingContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.tablet} {
    padding-right: ${rem(variables.gutterWidth / 2)};
    padding-left: ${rem(variables.gutterWidth / 2)};
  }
`

const OfferOverlayContainer = styled(LabelGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const FloatingOfferBadge = styled(OfferBadge)`
  margin: ${rem(16)};
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-template-rows: 50% 50%;
  gap: 8px;
  aspect-ratio: 2.7;
  position: relative;

  > :first-child,
  > ${FloatingOfferBadge} {
    grid-column: 1 / 2;
    grid-row: 1 / 3;
  }

  > ${FloatingOfferBadge} {
    align-self: start;
    justify-self: end;
  }
`

const GalleryButtons = styled(Group)`
  display: flex;
  pointer-events: auto;
  gap: ${rem(8)};
  justify-content: flex-end;
  position: absolute;
  bottom: ${rem(16)};
  right: ${rem(16)};
`

const OverlayLabelContainer = styled(LabelGroup)`
  position: absolute;
  top: ${rem(16)};
  left: ${rem(16)};
`

const TopRightActionContainer = styled(Group)`
  position: absolute;
  pointer-events: auto;
  top: ${rem(16)};
  right: ${rem(16)};
`

const StyledLayoutContainer = styled(LayoutContainer)`
  padding-top: 0;
  ${mediaQueryUp.tablet}{
    padding-top: ${rem(16)};
  }
`

const defaultImageProps: ImageParams = { tabletWidth: '400px', quality: 'good' }
const mainImageProps: ImageParams = { tabletWidth: '800px', quality: 'best' }

interface GalleryView {
  images: Array<App.Image>;
  videoIds: Array<string>;
  productType?: App.ProductType;
  partnershipLabel?: React.ReactNode;
  badge?: App.OfferBadge;
}

interface Props {
  offer?: App.AnyOffer;
  tourVariation?: Tours.V2OfferVariation;
  experience?: App.ExperienceOffer;
  shareModalConfig?: ShareConfig;
}

/**
 * An offer page specific gallery that will show the images/videos for the given
 * offer types supported
 */
function OfferPageGallery(props: Props) {
  const { offer, experience, tourVariation, shareModalConfig } = props

  const isMobileDevice = useAppSelector(state => isMobile(state.config.userAgent.osName))
  const showLightbox = useContext(LightBoxContext)
  const { partnership } = useContext(GeoContext)
  const velocityEnabled = partnership?.code === VIRGIN_VELOCITY_PARTNERSHIP_CODE

  const {
    images,
    videoIds,
    badge,
    productType,
    partnershipLabel,
  } = useMemo((): GalleryView => {
    if (isTourV2Offer(offer)) {
      return {
        videoIds: offer.video ? [offer.video.id] : [],
        images: tourVariation?.images ?? [],
        partnershipLabel: velocityEnabled ? <PartnershipLabel canEarn canUse/> : undefined,
      }
    } else if (experience) {
      const velocityFeatures = getApplicableVelocityFeatures(experience.price)
      return {
        videoIds: experience.video ? [experience.video] : [],
        images: experience.images,
        productType: undefined,
        badge: undefined,
        partnershipLabel: velocityEnabled ? <PartnershipLabel {...velocityFeatures} /> : undefined,
      }
    } else if (isLEOffer(offer)) {
      let productType: App.ProductType | undefined = offer.productType
      if (isLPCTacticalEnabled() && offer.hasTactical) {
        productType = 'limited_time_special'
      } else if (offer.isPartnerProperty) {
        productType = undefined
      }
      return {
        videoIds: offer.vimeoVideoId ? [offer.vimeoVideoId] : [],
        badge: offer.badge,
        images: offer.images,
        productType,
        partnershipLabel: <OfferPartnershipLabel offer={offer} />,
      }
    } else if (isBedbank(offer)) {
      return {
        videoIds: offer.videos?.filter(vid => vid.type === 'vimeo').map(vid => vid.id) ?? [],
        badge: undefined,
        images: offer.images,
        productType: offer.promotions.length > 0 ? undefined : offer.productType,
      }
    } else if (isCruiseOffer(offer)) {
      const velocityFeatures = getApplicableVelocityFeatures(offer.mainDepartureDetails.lowestOverallPriceDetails.total)

      return {
        images: offer.images,
        productType: offer.productType,
        badge: undefined,
        videoIds: [],
        partnershipLabel: velocityEnabled ? <PartnershipLabel {...velocityFeatures} /> : undefined,
      }
    }
    return {
      images: [],
      videoIds: [],
    }
  }, [experience, offer, tourVariation, velocityEnabled])

  const interstitials: Array<ImageCarouselInterstitial> | undefined = useMemo(() => {
    if (videoIds.length > 0) {
      return [
        ...videoIds.map((videoId, index) => ({
          id: 'video',
          position: 2 + index,
          element: <VimeoWrapper onClick={() => showLightbox(<VideoLightbox videoId={videoId} playOnMount />)}>
            <VimeoVideo vimeoVideoId={videoId} controls={false} />
            <PlayButton>
              <LinePlayIcon />
            </PlayButton>
          </VimeoWrapper>,
        })),
      ]
    }

    return undefined
  }, [showLightbox, videoIds])

  const showVideoLightbox = useCallback(() => {
    if (videoIds.length > 0) {
      showLightbox(<VideoLightbox videoId={videoIds[0]} playOnMount />)
    }
  }, [showLightbox, videoIds])

  const [galleryMode, galleryImages] = useMemo<[] | ['grid', Array<App.Image>] | ['carousel', Array<App.Image>]>(() => {
    if (!images.length) return []

    if (!isMobileDevice && images.length >= 3) {
      return ['grid', take(images, 3)]
    }

    return ['carousel', images]
  }, [isMobileDevice, images],
  )

  const handleImageClick = useCallback<MouseEventHandler<HTMLPictureElement>>((event) => {
    const imageIndex = Number(event.currentTarget.dataset.index) || 0
    showLightbox(<ImageGalleryLightbox images={images} defaultIndex={imageIndex}/>)
  }, [images, showLightbox])

  return <Pane>
    <StyledLayoutContainer>
      {galleryMode === 'grid' &&
        <GridContainer>
          {galleryImages.map((image, imageIndex) =>
            <Clickable
              key={image.id ?? image.url}
              onClick={handleImageClick}
              data-index={imageIndex}
            >
              {image.id && <ResponsiveImage
              id={image.id}
              fit="center"
              {...(imageIndex === 0 ? mainImageProps : defaultImageProps)}
            />}
              {image.url && <Image
              image={image}
              fit="center"
            />}
            </Clickable>)}
          <OverlayLabelContainer>
            {productType && <ProductTypeLabel productType={productType} />}
            {partnershipLabel}
          </OverlayLabelContainer>
          <GalleryButtons direction="horizontal">
            {videoIds.length > 0 &&
              <TextButton
                kind="secondary"
                variant="dark"
                startIcon={<SolidPlayIcon />}
                onClick={showVideoLightbox}
              >
                Play video
              </TextButton>
            }
            <CSSBreakpoint min="desktop">
              {images.length > 1 &&
                <TextButton
                  kind="secondary"
                  variant="dark"
                  startIcon={<LineImagesIcon />}
                  onClick={handleImageClick}
                >
                  View gallery
                </TextButton>
              }
            </CSSBreakpoint>
          </GalleryButtons>
          {badge && <FloatingOfferBadge badge={badge} />}
        </GridContainer>
      }
    </StyledLayoutContainer>
    {galleryMode === 'carousel' &&
      <MobileHuggingContainer>
        <ImageGallery
          topLeft={<OfferOverlayContainer>
            {productType && <ProductTypeLabel productType={productType} />}
            {partnershipLabel}
          </OfferOverlayContainer>}
            topRight={
              <>
                {shareModalConfig && (
                  <CSSBreakpoint max="mobile">
                    <TopRightActionContainer direction="horizontal" gap={8}>
                      <ShareModal shareConfig={shareModalConfig}>
                        <ShareWithFriendsButton iconOnly />
                      </ShareModal>
                      {offer && (
                        <TripGuard>
                          {isTourV2Offer(offer) && <TourBookmarkButton offer={offer} selectedVariation={tourVariation} iconOnly />}
                          {!isTourV2Offer(offer) && <BookmarkButton offer={offer} iconOnly />}
                        </TripGuard>
                      )}
                      {experience && (
                        <TripGuard>
                          <ExperienceBookmarkButton offer={experience} iconOnly />
                        </TripGuard>
                      )}
                    </TopRightActionContainer>
                  </CSSBreakpoint>
                )}
                {badge && (
                  <CSSBreakpoint min="desktop">
                    <OfferBadge badge={badge} />
                  </CSSBreakpoint>
                )}
              </>
            }
            interstitials={interstitials}
            images={galleryImages}
            eagerLoadFirstImage
            videoId={videoIds[0]}
            arrowBehaviour="always-visible"
          />
      </MobileHuggingContainer>
    }
  </Pane>
}

export default OfferPageGallery
